import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileCheck, AlertCircle, CheckCircle } from 'lucide-react';

import { API_URL } from '../config';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    if (password.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/reset-password`, { 
        token, 
        new_password: password 
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel reset della password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Link Non Valido</h1>
          <p className="text-muted-foreground mb-6">
            Il link per reimpostare la password non è valido o è scaduto.
          </p>
          <Link to="/forgot-password">
            <Button className="swiss-btn-primary">
              Richiedi Nuovo Link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <div className="w-10 h-10 bg-forest rounded-sm flex items-center justify-center">
            <FileCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="text-xl font-bold tracking-tight text-forest">BustaControllo</span>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-200">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Password Aggiornata!</h1>
              <p className="text-muted-foreground mb-6">
                La tua password è stata reimpostata con successo. Verrai reindirizzato al login...
              </p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold mb-2">Reimposta Password</h1>
              <p className="text-muted-foreground mb-6">
                Inserisci la tua nuova password.
              </p>

              {error && (
                <div className="flex items-center gap-2 p-4 mb-6 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="label-uppercase">Nuova Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimo 6 caratteri"
                    className="swiss-input h-12"
                    data-testid="reset-password"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="label-uppercase">Conferma Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Ripeti la password"
                    className="swiss-input h-12"
                    data-testid="reset-confirm-password"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="swiss-btn-primary w-full h-12"
                  disabled={loading}
                  data-testid="reset-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner" />
                      Salvataggio...
                    </span>
                  ) : (
                    'Salva Nuova Password'
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
        
        {/* Copyright */}
        <p className="mt-6 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} BustaControllo. Tutti i diritti riservati.
        </p>
      </div>
    </div>
  );
}
